import { z } from "zod";
import moment from "moment-timezone";
import {
  ContractType,
  Commodities,
  BaseContractValidation,
  BasePriceReownValidation,
  RangeValidation,
  RangePriceReownValidation,
  RangeWDUValidation,
  ReOwnWDUValidation,
  MinMaxValidation,
  HedgePlusValidation,
  PremiumValidation,
  DoubleUpValidation,
  MinPriceValidation,
  MinPricePutSpreadValidation,
  MinimumPriceValidation,
  AccumulatorValidation,
  AccumulatorGtdQtyValidation,
  MinMaxDoubleUpValidation,
} from "@/lib/api/otc";
import {
  isReownContractType,
  parseContractRefGroups,
  validateContractRefGroups,
} from "./contract-reference-utils";

// ---------------------------------------------------------------------------
// Form-level extension of the API base schema.
//
// Adds fields that exist on the form but are missing from the API schemas:
//   - Adjusted rows 2 & 3
//   - Derived/computed fields (margin, margin_amount, etc.)
//   - superRefine for cross-field rules (canola implied_fx_amount)
//
// The API schemas stay untouched.  These form schemas are the complete
// source of truth for the OTC form: visibility, required, validation,
// and field stripping on submit.
// ---------------------------------------------------------------------------

/**
 * Extra fields shared by ALL contract types (added to the base).
 */
const formExtraFields = {
  has_contract_adjustment: z.boolean().optional(),

  // Adjusted row 2
  adj_option_premium_two: z.string().optional(),
  adj_contract_fee_two: z.string().optional(),
  adj_margin_two: z.string().optional(),
  adj_margin_amount_two: z.string().optional(),
  adjusted_date_two: z.string().optional(),

  // Adjusted row 3
  adj_option_premium_three: z.string().optional(),
  adj_contract_fee_three: z.string().optional(),
  adj_margin_three: z.string().optional(),
  adj_margin_amount_three: z.string().optional(),
  adjusted_date_three: z.string().optional(),

  // Adjusted row 4
  adj_option_premium_four: z.string().optional(),
  adj_contract_fee_four: z.string().optional(),
  adj_margin_four: z.string().optional(),
  adj_margin_amount_four: z.string().optional(),
  adjusted_date_four: z.string().optional(),

  // Adjusted row 5
  adj_option_premium_five: z.string().optional(),
  adj_contract_fee_five: z.string().optional(),
  adj_margin_five: z.string().optional(),
  adj_margin_amount_five: z.string().optional(),
  adjusted_date_five: z.string().optional(),
};

/**
 * Shared cross-field refinements applied to all form schemas.
 *
 * IMPORTANT: These rules enforce validation at submit time, but they do NOT
 * affect the "Required" label in the UI (which is derived from the static
 * Zod schema shape).  For any field whose required status is conditional
 * (i.e. enforced here via superRefine rather than the schema shape), you
 * must ALSO set `requiredOverride` in the field-meta (see field-meta.tsx)
 * so the UI label stays in sync with the validation rule.
 */
function sharedSuperRefine(data: Record<string, unknown>, ctx: z.RefinementCtx) {
  // implied_fx_amount is required when commodity is Canola.
  // UI "Required" label is controlled by requiredOverride in field-meta.tsx.
  if (
    (data.commodity as string)?.toLocaleUpperCase() === Commodities.Canola &&
    (!data.implied_fx_amount || (data.implied_fx_amount as string).trim() === "")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["implied_fx_amount"],
    });
  }

  // Reown contract reference: validate that all groups are complete.
  // Contract reference is optional for reown contracts, but if groups are
  // partially filled they must be internally consistent.
  if (isReownContractType(data.contract_type as string | undefined)) {
    const groups = parseContractRefGroups(data.contract_reference as string | undefined);
    const groupErrors = validateContractRefGroups(groups, { requireFirstGroup: false });
    if (groupErrors !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Incomplete contract reference groups",
        path: ["contract_reference"],
      });
    }
  }

  // Adj Date is required when the corresponding Adj Option Premium has a value.
  // UI "Required" label is controlled by requiredOverride in field-meta.tsx.
  const adjPairs: Array<{ premium: string; date: string }> = [
    { premium: "adj_option_premium", date: "last_adjusted_date" },
    { premium: "adj_option_premium_two", date: "adjusted_date_two" },
    { premium: "adj_option_premium_three", date: "adjusted_date_three" },
    { premium: "adj_option_premium_four", date: "adjusted_date_four" },
    { premium: "adj_option_premium_five", date: "adjusted_date_five" },
  ];
  for (const { premium, date } of adjPairs) {
    const premiumVal = data[premium];
    const hasPremium =
      premiumVal !== undefined &&
      premiumVal !== null &&
      !(typeof premiumVal === "string" && premiumVal.trim() === "");
    const dateVal = data[date];
    const hasDate =
      dateVal !== undefined &&
      dateVal !== null &&
      !(typeof dateVal === "string" && (dateVal as string).trim() === "");
    if (hasPremium && !hasDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: [date],
      });
    }
  }

  // Final futures price row: all three of final_futures_price / final_futures_expected_price /
  // final_futures_price_date must be set together, or all absent.
  // UI "Required" label is controlled by requiredOverride in field-meta.tsx.
  {
    const isEmpty = (v: unknown): boolean =>
      v === undefined || v === null || (typeof v === "string" && v.trim() === "");
    // The date arrives here serialized to a string via serializeDate. A malformed
    // Date (e.g. new Date("")) serializes to "NaN/NaN/NaN" which is non-empty;
    // use moment to detect that case and treat it as not set.
    const isDateSet = (v: unknown): boolean => {
      if (isEmpty(v)) return false;
      if (typeof v === "string") return moment(v, ["YYYY/MM/DD", "YYYY-MM-DD", moment.ISO_8601]).isValid();
      return moment(v as Date).isValid();
    };
    const priceSet = !isEmpty(data.final_futures_price);
    const expectedSet = !isEmpty(data.final_futures_expected_price);
    const dateSet = isDateSet(data.final_futures_price_date);
    const anySet = priceSet || expectedSet || dateSet;
    const allSet = priceSet && expectedSet && dateSet;
    if (anySet && !allSet) {
      if (!priceSet) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["final_futures_price"],
        });
      }
      if (!expectedSet) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["final_futures_expected_price"],
        });
      }
      if (!dateSet) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["final_futures_price_date"],
        });
      }
    }
  }

  // shipment_end_date must be after shipment_start_date
  if (data.shipment_start_date && data.shipment_end_date) {
    const start = new Date(data.shipment_start_date as string);
    const end = new Date(data.shipment_end_date as string);
    if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end <= start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date must be after start date",
        path: ["shipment_end_date"],
      });
    }
  }
}

/**
 * The form-level base schema: API base + extra fields + cross-field refines.
 */
const FormBaseValidation = BaseContractValidation
  .extend(formExtraFields)
  .superRefine(sharedSuperRefine);

// ---------------------------------------------------------------------------
// Helper: extend a variant schema with the form extras + canola refine.
//
// Each API variant schema already extends BaseContractValidation.  We
// re-extend it with the form extra fields and apply the same superRefine.
// ---------------------------------------------------------------------------

function formVariant(
  apiSchema: z.ZodObject<z.ZodRawShape>,
): z.ZodEffects<z.ZodObject<z.ZodRawShape>> {
  return apiSchema.extend(formExtraFields).superRefine(sharedSuperRefine);
}

// ---------------------------------------------------------------------------
// Form variant schemas – one per contract type
// ---------------------------------------------------------------------------

export const FormBasePriceReownValidation = formVariant(BasePriceReownValidation as z.ZodObject<z.ZodRawShape>);
export const FormRangeValidation = formVariant(RangeValidation as z.ZodObject<z.ZodRawShape>);
export const FormRangePriceReownValidation = formVariant(RangePriceReownValidation as z.ZodObject<z.ZodRawShape>);
export const FormRangeWDUValidation = formVariant(RangeWDUValidation as z.ZodObject<z.ZodRawShape>);
export const FormReOwnWDUValidation = formVariant(ReOwnWDUValidation as z.ZodObject<z.ZodRawShape>);
export const FormMinMaxValidation = formVariant(MinMaxValidation as z.ZodObject<z.ZodRawShape>);
export const FormHedgePlusValidation = formVariant(HedgePlusValidation as z.ZodObject<z.ZodRawShape>);
export const FormPremiumValidation = formVariant(PremiumValidation as z.ZodObject<z.ZodRawShape>);
export const FormDoubleUpValidation = formVariant(DoubleUpValidation as z.ZodObject<z.ZodRawShape>);
export const FormMinPriceValidation = formVariant(MinPriceValidation as z.ZodObject<z.ZodRawShape>);
export const FormMinPricePutSpreadValidation = formVariant(MinPricePutSpreadValidation as z.ZodObject<z.ZodRawShape>);
export const FormMinimumPriceValidation = formVariant(MinimumPriceValidation as z.ZodObject<z.ZodRawShape>);
export const FormAccumulatorValidation = formVariant(AccumulatorValidation as z.ZodObject<z.ZodRawShape>);
export const FormAccumulatorGtdQtyValidation = formVariant(AccumulatorGtdQtyValidation as z.ZodObject<z.ZodRawShape>);
export const FormMinMaxDoubleUpValidation = formVariant(MinMaxDoubleUpValidation as z.ZodObject<z.ZodRawShape>);

// ---------------------------------------------------------------------------
// The form base (used by SchemaConfig for field introspection).
// This is the ZodObject *before* superRefine so the form library can
// inspect .shape for field names, required status, etc.
// ---------------------------------------------------------------------------

export const FormBaseShape = BaseContractValidation.extend(formExtraFields);
